import { UntilDestroy } from '@ngneat/until-destroy';
import { Component, Inject } from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import { Invite } from '@models/businesses';
import { ShortDateFormat } from '@models/businessSettings/businessFormatTypes';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@services/ui/snackbar.service';
import {
	AcceptInviteRequestData,
	AuthenticationService,
} from '@services/authentication/authentication.service';
import { ICON } from '@core/util/icon';
import LogRocket from 'logrocket';
import { DateToBusinessShortDate } from '@core/util/datetime';
import { firstValueFrom } from 'rxjs';

export interface ViewInviteDialogData {
	invite: Invite;
	firstName: string;
	lastName: string;
}

@UntilDestroy()
@Component({
	selector: 'app-view-invite-dialog',
	templateUrl: './view-invite-dialog.component.html',
	animations: [fade(100), openClose()],
	standalone: false,
})
export class ViewInviteDialogComponent {
	protected readonly DateToBusinessShortDate = DateToBusinessShortDate;
	protected readonly ShortDateFormat = ShortDateFormat;

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: ViewInviteDialogData,
		private snackbarService: SnackbarService,
		private dialogRef: MatDialogRef<ViewInviteDialogComponent>,
		private authenticationService: AuthenticationService,
	) {}

	async declineInvite() {
		await this.errorHandler('decline');
	}

	async acceptInvite() {
		await this.errorHandler('accept');
	}

	private async errorHandler(action: 'accept' | 'decline') {
		try {
			const requestData: AcceptInviteRequestData = {
				inviteId: this.data.invite.id,
				firstName: this.data.firstName,
				lastName: this.data.lastName,
			};

			switch (action) {
				case 'accept':
					await firstValueFrom(
						this.authenticationService.acceptInvite(requestData),
					);
					break;
				case 'decline':
					await firstValueFrom(
						this.authenticationService.declineInvite(this.data.invite.id),
					);
					break;
			}

			this.dialogRef.close();
			this.snackbarService.openSnackBar({
				text: `Invitation ${action === 'accept' ? 'accepted' : 'declined'} for ${this.data.invite.businessName}!`,
				type: action === 'accept' ? 'success' : 'error',
				icon: action === 'accept' ? ICON.CIRCLE_CHECK : ICON.CIRCLE_X,
			});
		} catch (error) {
			LogRocket.captureException(error);
			this.snackbarService.openSnackBar({
				text: `Failed to ${action} invite, please try again. If error persists please contact support`,
				type: 'error',
				icon: ICON.CIRCLE_X,
			});
		}
	}
}
